import { createColumnHelper, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import paginationUtils from '../Utils/paginationUtils';
import Button from './Button';
import { Box, Grid, Pagination, styled } from '@mui/material';
import { cloneDeep } from 'lodash';
import AutoComplete from './AutoComplete/AutoCompleteSelect';
import ListViewSkeleton from './Loaders/ListViewSkeleton';
import Loader from './Loader';

interface Iprops {
  data: any[];
  columnHelper: any;
  defaultColumns: any[];
  paginationDetail: any;
  customAddButtonText?: any;
  onActionbarAction?: (event: MouseEvent, type: string, data: any) => void;
  showPaginationButtons?: boolean;
  paginationChange?: (paginationDetails) => void;
  showHeaderDropDown?: boolean;
  dropDownOptions?: any[];
  dropDownOnChange?: (value) => void;
  dropDownTitle?: string;
  showLoader?: boolean;
}

const DEFAULT_PAGINATION = {
  sortBy: '',
  descending: true,
  rowsPerPage: 10,
  page: 1,
  totalItems: 0,
  pages: 1,
};

const CommonTable = ({
  data = [],
  showLoader = false,
  columnHelper = createColumnHelper<typeof data>(),
  defaultColumns = [],
  paginationDetail = DEFAULT_PAGINATION,
  customAddButtonText = null,
  onActionbarAction,
  showPaginationButtons = false,
  paginationChange = null,
  showHeaderDropDown = false,
  dropDownOptions = [],
  dropDownOnChange = null,
  dropDownTitle = '',
  ...props
}: any) => {
  if (!data) data = [];
  if (!columnHelper) columnHelper = createColumnHelper<typeof data>();
  if (!defaultColumns) defaultColumns = [];
  if (!paginationDetail) paginationDetail = DEFAULT_PAGINATION;
  const [columns, setColumns] = useState<typeof defaultColumns>(defaultColumns);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: paginationDetail.rowsPerPage,
  });

  const [paginationSummary, setPaginationSummary] = useState(
    paginationUtils.getPaginationSummary(paginationDetail, paginationDetail.totalItems)
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination: pagination,
    },
  });

  const onTableActionbarAction = (evt, type) => {
    // console.log('onTableActionbarAction', evt);
    // if (onActionbarAction) onActionbarAction(evt, type);
  };

  const onCustomAddButtonClick = (evt) => {
    // console.log('onCustomAddButtonClick', evt);
    onActionbarAction(evt, 'add', null);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination({
      pageIndex: value - 1,
      pageSize: paginationDetail.rowsPerPage,
    });
    let paginationDetails = cloneDeep(paginationDetail);
    paginationDetails.page = value;
    if (paginationChange) paginationChange(paginationDetails);
  };

  useEffect(() => {
    if (!paginationDetail) paginationDetail = DEFAULT_PAGINATION;
    setPaginationSummary(paginationUtils.getPaginationSummary(paginationDetail, paginationDetail.totalItems));
  }, [data, paginationDetail]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#e8e7e4',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#f4f3f0',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  return (
    <div className="table-container">
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {customAddButtonText && (
            <Button variant="contained" size="small" rounded onClick={onCustomAddButtonClick}>
              <i className="nc-icon nc-simple-add" style={{ marginRight: '5px' }} /> {customAddButtonText}
            </Button>
          )}
        </Grid>
        {showHeaderDropDown && (
          <>
            <Grid item style={{ minWidth: '100px' }} xs={2}>
              <AutoComplete
                id="select-visibility"
                items={dropDownOptions}
                itemText="name"
                itemKey="id"
                labelText={dropDownTitle}
                placeHolderText="Select option"
                allowEmptyValue={true}
                multiSelect={false}
                onChange={dropDownOnChange}
              />
            </Grid>
            {props.repoDropdownOptions && (
              <Grid item xs={2} style={{ minWidth: '100px' }}>
                <AutoComplete
                  id="select-repository"
                  items={props.repoDropdownOptions}
                  itemText="name"
                  itemKey="id"
                  labelText={props.repoDropdownTitle}
                  placeHolderText="Select option"
                  allowEmptyValue={true}
                  multiSelect={false}
                  onChange={(item) => dropDownOnChange(item, 'repository')}
                />
              </Grid>
            )}
            {props.modelTypeDropdownOptions && (
              <Grid item xs={2} style={{ minWidth: '100px' }}>
                <AutoComplete
                  id="select-type"
                  items={props.modelTypeDropdownOptions}
                  itemText="name"
                  itemKey="id"
                  labelText={props.modelTypeDropdownTitle}
                  placeHolderText="Select option"
                  allowEmptyValue={true}
                  multiSelect={false}
                  onChange={(item) => dropDownOnChange(item, 'modelType')}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
      {showLoader && <ListViewSkeleton listLength={10} />}
      {!showLoader && (
        <>
          <TableContainer component={Paper} className="table-wrapper">
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} style={{ backgroundColor: '#f4f3f0' }}>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        style={{
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          width: header.column.columnDef.width,
                          maxWidth: header.column.columnDef.width,
                          minWidth: header.column.columnDef.width,
                          textAlign: header.column.columnDef.align === 'left' ? 'left' : 'center',
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <StyledTableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        style={{
                          paddingLeft: cell.column.columnDef.align === 'left' ? '8px' : '0px',
                          paddingRight: cell.column.columnDef.align === 'right' ? '8px' : '0px',
                          textAlign: cell.column.columnDef.align || 'center',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {paginationSummary && (
                <div className="pagination-stats">
                  Showing {paginationSummary.from} &mdash; {paginationSummary.to} of {paginationSummary.total}
                </div>
              )}
            </Grid>
            <Grid item xs={6}>
              {showPaginationButtons && (
                <Pagination
                  className="pagination-steps"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                  count={paginationDetail.pages}
                  page={paginationDetail.page}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handlePageChange}
                  sx={{ marginBottom: '20px !important', float: 'right' }}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default CommonTable;
